import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  alpha,
  useTheme,
  Icon,
  Tooltip,
  FormHelperText,
  InputAdornment,
  Chip,
} from "@mui/material";
import { AppDatePicker } from "../../../../../components/AppDatePicker";
import { useBranch } from "../../../components/BranchContext";
import dayjs, { Dayjs } from "dayjs";
import { accountingService } from "../../../../../services/accounting";
import { IBranch, IListBranchesRequest } from "../../../accounting.interface";
import { toast } from "react-toastify";
import {
  CURRENCIES,
  FormData,
  JournalLineForm,
  JournalTotals,
  FormValidationState,
  createJournalTypeMenuItems,
} from "../utils";

interface JournalHeaderSectionProps {
  formData: FormData;
  errors: Record<string, string>;
  onFieldChange: (field: keyof FormData, value: any) => void;
}

export const JournalHeaderSection: React.FC<JournalHeaderSectionProps> = ({
  formData,
  errors,
  onFieldChange,
}) => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();
  const [branches, setBranches] = useState<IBranch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const journalTypeMenuItems = React.useMemo(
    () => createJournalTypeMenuItems(),
    []
  );

  // Fetch branches from API
  const fetchBranches = useCallback(async () => {
    setBranchesLoading(true);
    try {
      const requestData: IListBranchesRequest = {
        sortField: "name",
        sortOrder: "asc",
      };

      const response = await accountingService.listBranches(requestData);
      setBranches(
        response.data.branches.filter((branch) => !branch.is_deleted)
      );
    } catch (error: any) {
      console.error("Failed to fetch branches:", error);
      toast.error("Failed to load branches");
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  }, []);

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Get branch name for display
  const getBranchDisplayValue = () => {
    if (selectedBranch === "organization") {
      return "Organization Level (All Branches)";
    }
    const branch = branches.find((b) => b.branch_id === selectedBranch);
    return branch
      ? branch.name
      : branchesLoading
      ? "Loading..."
      : "Unknown Branch";
  };

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
      >
        Journal Header
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AppDatePicker
            label="Journal Date *"
            value={formData.entry_date ? dayjs(formData.entry_date) : null}
            onChange={(value: Dayjs | null) =>
              onFieldChange("entry_date", value ? value.toDate() : null)
            }
            helperText={errors.entry_date}
            fullWidth={true}
            required={true}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Branch"
            value={getBranchDisplayValue()}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon
                    sx={{
                      color:
                        selectedBranch === "organization"
                          ? "warning.main"
                          : "text.secondary",
                    }}
                  >
                    {selectedBranch === "organization" ? "business" : "store"}
                  </Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor:
                  selectedBranch === "organization"
                    ? alpha(theme.palette.warning.main, 0.05)
                    : alpha(theme.palette.action.hover, 0.05),
                borderColor:
                  selectedBranch === "organization"
                    ? alpha(theme.palette.warning.main, 0.3)
                    : undefined,
              },
            }}
            helperText={
              selectedBranch === "organization"
                ? "⚠️ Please select a specific branch to create journal entries"
                : "Selected from header branch selector"
            }
            error={selectedBranch === "organization"}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={formData.currency}
              onChange={(e) => onFieldChange("currency", e.target.value)}
              label="Currency"
              disabled={CURRENCIES.length === 1}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor:
                    CURRENCIES.length === 1
                      ? alpha(theme.palette.action.hover, 0.05)
                      : "transparent",
                },
              }}
            >
              {CURRENCIES.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.label}
                </MenuItem>
              ))}
            </Select>
            {CURRENCIES.length === 1 && (
              <FormHelperText>
                Only BHD is available in this system
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Journal Type</InputLabel>
            <Select
              value={formData.journal_type}
              onChange={(e) => onFieldChange("journal_type", e.target.value)}
              label="Journal Type"
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    overflow: "auto",
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                    },
                  },
                },
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Select a journal type
                </Typography>
              </MenuItem>
              {journalTypeMenuItems}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Narration *"
            multiline
            rows={3}
            value={formData.narration}
            onChange={(e) => onFieldChange("narration", e.target.value)}
            error={!!errors.narration}
            helperText={
              errors.narration ||
              "A meaningful description of the purpose of this journal"
            }
            placeholder="e.g., Allocate bonus to staff Q2"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Reference ID"
            value={formData.reference_id}
            onChange={(e) => onFieldChange("reference_id", e.target.value)}
            placeholder="Document, invoice, or external reference"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

interface JournalLinesSectionProps {
  formData: FormData;
  lineErrors: Record<string, Record<string, string>>;
  postableAccounts: any[];
  onLineChange: (
    lineId: string,
    field: keyof JournalLineForm,
    value: any
  ) => void;
  onAccountSelect: (lineId: string, accountId: string) => void;
  onAmountChange: (
    lineId: string,
    field: "debit" | "credit",
    value: number | null
  ) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
}

export const JournalLinesSection: React.FC<JournalLinesSectionProps> = ({
  formData,
  lineErrors,
  postableAccounts,
  onLineChange,
  onAccountSelect,
  onAmountChange,
  onAddLine,
  onRemoveLine,
}) => {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Journal Lines
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Icon>add</Icon>}
          onClick={onAddLine}
          size="small"
        >
          Add Line
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Account *</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Debit
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Credit
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.journal_lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  <FormControl fullWidth error={!!lineErrors[line.id]?.account}>
                    <Select
                      value={line.account_id}
                      onChange={(e) => onAccountSelect(line.id, e.target.value)}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="" disabled>
                        Select Account
                      </MenuItem>
                      {postableAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {account.accountCode} - {account.accountName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {account.accountType}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {lineErrors[line.id]?.account && (
                      <FormHelperText>
                        {lineErrors[line.id].account}
                      </FormHelperText>
                    )}
                  </FormControl>
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    value={line.description}
                    onChange={(e) =>
                      onLineChange(line.id, "description", e.target.value)
                    }
                    placeholder="Line description"
                    fullWidth
                  />
                </TableCell>

                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={line.debit || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : null;
                      onAmountChange(line.id, "debit", value);
                    }}
                    error={!!lineErrors[line.id]?.debit}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formData.currency}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 120 }}
                  />
                </TableCell>

                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={line.credit || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : null;
                      onAmountChange(line.id, "credit", value);
                    }}
                    error={!!lineErrors[line.id]?.credit}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formData.currency}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 120 }}
                  />
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="Remove Line">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveLine(line.id)}
                        disabled={formData.journal_lines.length <= 2}
                        color="error"
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Line errors */}
      {Object.keys(lineErrors).length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Please fix the following errors:
          </Typography>
          {Object.entries(lineErrors).map(([lineId, errors]) => (
            <Box key={lineId} sx={{ ml: 2 }}>
              {Object.entries(errors).map(([field, error]) => (
                <Typography
                  key={field}
                  variant="body2"
                  sx={{ color: "error.main" }}
                >
                  • Line{" "}
                  {formData.journal_lines.findIndex((l) => l.id === lineId) + 1}
                  : {error}
                </Typography>
              ))}
            </Box>
          ))}
        </Alert>
      )}
    </Paper>
  );
};

interface TotalsValidationSectionProps {
  formData: FormData;
  totals: JournalTotals;
  validationState: FormValidationState;
  errors: Record<string, string>;
}

export const TotalsValidationSection: React.FC<
  TotalsValidationSectionProps
> = ({ formData, totals, validationState, errors }) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        bgcolor: totals.isBalanced
          ? alpha(theme.palette.success.main, 0.05)
          : alpha(theme.palette.error.main, 0.05),
        border: `1px solid ${
          totals.isBalanced
            ? alpha(theme.palette.success.main, 0.2)
            : alpha(theme.palette.error.main, 0.2)
        }`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Totals & Validation
        </Typography>

        {/* Professional status indicators */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            size="small"
            label={
              validationState.hasRequiredFields
                ? "Required Fields ✓"
                : "Missing Required Fields"
            }
            color={validationState.hasRequiredFields ? "success" : "error"}
            variant="outlined"
          />
          <Chip
            size="small"
            label={
              validationState.hasValidLines ? "Valid Lines ✓" : "Invalid Lines"
            }
            color={validationState.hasValidLines ? "success" : "warning"}
            variant="outlined"
          />
          <Chip
            size="small"
            label={validationState.isBalanced ? "Balanced ✓" : "Unbalanced"}
            color={validationState.isBalanced ? "success" : "error"}
            variant="outlined"
          />
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "success.main", fontWeight: 600 }}
            >
              {formData.currency} {totals.totalDebit.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Debit
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "error.main", fontWeight: 600 }}
            >
              {formData.currency} {totals.totalCredit.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Credit
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: totals.isBalanced ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {formData.currency} {Math.abs(totals.difference).toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {totals.isBalanced ? "Balanced" : "Difference"}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Professional action status */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Available Actions:
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Typography
            variant="body2"
            sx={{
              color: validationState.canSaveAsDraft
                ? "success.main"
                : "text.disabled",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>
              {validationState.canSaveAsDraft ? "check_circle" : "cancel"}
            </Icon>
            Save as Draft
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: validationState.canPost ? "success.main" : "text.disabled",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>
              {validationState.canPost ? "check_circle" : "cancel"}
            </Icon>
            Post Entry
          </Typography>
        </Box>
      </Box>

      {errors.balance && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.balance}
        </Alert>
      )}
    </Paper>
  );
};
